import { NullableDate } from "../types/common";
export const toUnixTimeStamp = (date: Date) =>
    Math.round(date.valueOf() / 1000);

export const toValidUnixTimeStamp = (date?: NullableDate) => {
    return date ? Math.round(date.valueOf() / 1000) : undefined;
};

export const addDaysOnDate = (
    currentDate: Date = new Date(),
    daysToAdd: number = 1,
    hoursToAdd: number = 0,
    minsToAdd: number = 0,
    secsToAdd: number = 0,
): Date => {
    const nextDay: Date = new Date(currentDate);
    nextDay.setDate(nextDay.getDate() + daysToAdd);

    if (hoursToAdd) {
        nextDay.setHours(nextDay.getHours() + hoursToAdd);
    }
    if (minsToAdd) {
        nextDay.setMinutes(nextDay.getMinutes() + minsToAdd);
    }
    if (secsToAdd) {
        nextDay.setSeconds(nextDay.getSeconds() + secsToAdd);
    }
    return nextDay;
};
export const isSameDate = (date1: Date, date2: Date) => {
    return date1.getTime() === date2.getTime();
};

export const getStartingTimeOfDate = (date: Date) => {
    const dt = new Date(date);
    dt.setHours(0, 0, 0, 0);
    return dt;
};
export const getEndingTimeOfDate = (date: Date) => {
    const dt = new Date(date);
    dt.setHours(23, 59, 59, 999);
    return dt;
};

/**
 * Formats a date string according to the provided format.
 * @param {string} inputDate - The input date string to be formatted.
 * @param {string} format - The custom format specifying how the date should be formatted.
 * @returns {string} The formatted date string.
 */

export const formatDate = (
    inputDate: string | number | Date,
    format: string,
    fallback?: string,
): string => {
    /**
     * (2023-09-16,DD/MM/YYYY)=> 16/09/2023
     * (2022-03-05,MMMM DD, YYYY)=> March 05, 2022
     * (2021-12-25,"YYYY [is the year] MM [is the month] DD [is the day]")=> '2021 is the year 12 is the month 25 is the day'
     * ("2021-12-25T18:45:30.500Z","YYYY-MM-DD hh:mm:ss.SSS")=> 2021-12-25 18:45:30.500
     */
    // Parse the input date using JavaScript's Date object

    let date: Date | undefined = undefined;

    if (typeof inputDate === "string" || typeof inputDate === "number") {
        date = new Date(inputDate);
    }
    if (inputDate instanceof Date) {
        date = inputDate;
    }

    // Check if the input date is valid
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return fallback ?? "Invalid Date";
    }

    const hours24 = date.getHours();
    const hours12 = hours24 % 12 || 12;
    const amOrPm = hours24 >= 12 ? "PM" : "AM";

    // Create a dictionary to replace format placeholders
    const formatOptions = {
        YYYY: date.getFullYear(),
        YY: date.getFullYear().toString().slice(-2),
        MMMM: new Intl.DateTimeFormat("en-US", { month: "long" }).format(date),
        MMM: new Intl.DateTimeFormat("en-US", { month: "short" }).format(date),
        MM: String(date.getMonth() + 1).padStart(2, "0"),
        M: String(date.getMonth() + 1),
        DDDD: new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(
            date,
        ),
        DDD: new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(
            date,
        ),
        DD: String(date.getDate()).padStart(2, "0"),
        D: String(date.getDate()),
        hh: String(hours12).padStart(2, "0"),
        h: String(hours12),
        hh24: String(hours24).padStart(2, "0"),
        h24: String(hours24),
        mm: String(date.getMinutes()).padStart(2, "0"),
        m: String(date.getMinutes()),
        ss: String(date.getSeconds()).padStart(2, "0"),
        s: String(date.getSeconds()),
        SSS: String(date.getMilliseconds()).padStart(3, "0"),
        S: String(date.getMilliseconds()).slice(0, 1),
        a: amOrPm,
        z: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    // Update the regular expression pattern to match placeholders as exact words
    const pattern = new RegExp(
        "\\b(?:" + Object.keys(formatOptions).join("|") + ")\\b",
        "g",
    );

    const formattedDate = format.replace(
        pattern,
        (match) => String(formatOptions[match as keyof typeof formatOptions]),
    );

    return formattedDate;
};

export function milliseconds(
    days: number = 0,
    hours: number = 0,
    minutes: number = 0,
    seconds: number = 0,
): number {
    const totalSeconds: number =
        (days * 24 + hours) * 3600 + minutes * 60 + seconds;
    const milliseconds: number = totalSeconds * 1000;
    return milliseconds;
}

type IRelativeTimeUnitMap = Record<Intl.RelativeTimeFormatUnit, number>;

export function getRelativeTimeString(
    date: Date | number,
    units?: IRelativeTimeUnitMap,
) {
    const timeUnitsInMs = {
        year: milliseconds(365),
        day: milliseconds(1),
        hour: milliseconds(0, 1),
        minute: milliseconds(0, 0, 1),
        second: milliseconds(0, 0, 0, 60),
        ...units,
    } as IRelativeTimeUnitMap;

    const rtf = new Intl.RelativeTimeFormat("en", { style: "narrow" });
    const timeMs = typeof date === "number" ? date : date.getTime();
    if (isNaN(timeMs)) {
        return "";
    }

    const delta = timeMs - Date.now();
    for (const [unit, amount] of Object.entries(timeUnitsInMs)) {
        if (Math.abs(delta) > amount) {
            return rtf.format(Math.round(delta / amount), unit as any);
        }
    }
    return "Just Now";
}

export const getFilterTimeString = (
    start?: NullableDate,
    end?: NullableDate,
) => {
    if (start || end) {
        const f = {} as any;
        if (start) {
            f["min"] = toUnixTimeStamp(start);
        }
        if (end) {
            f["max"] = toUnixTimeStamp(end);
        }

        return JSON.stringify(f);
    }
    return undefined;
};

export const getFilterMinMaxString = (
    start?: NullableDate,
    end?: NullableDate,
) => {
    if (start || end) {
        const f = {} as any;
        if (start) {
            f["min"] = toUnixTimeStamp(getStartingTimeOfDate(start));
        }
        if (end) {
            f["max"] = toUnixTimeStamp(getEndingTimeOfDate(end));
        }

        return JSON.stringify(f);
    }
    return undefined;
};

const offsetDate = (offsetDays: number): Date =>
    addDaysOnDate(new Date(), offsetDays);

const offsetMonth = (offsetMonths: number): Date => {
    const dt = new Date();
    dt.setMonth(dt.getMonth() + offsetMonths);
    return dt;
};

export const CommonDateTime = {
    yesterday: {
        min: () => getStartingTimeOfDate(addDaysOnDate(new Date(), -1)),
        max: () => getEndingTimeOfDate(addDaysOnDate(new Date(), -1)),
    },
    today: {
        min: () => getStartingTimeOfDate(new Date()),
        max: () => getEndingTimeOfDate(new Date()),
    },
    tomorrow: {
        min: () => getStartingTimeOfDate(addDaysOnDate(new Date(), 1)),
        max: () => getEndingTimeOfDate(addDaysOnDate(new Date(), 1)),
    },
    nthDate: {
        min: (n: number) => getStartingTimeOfDate(offsetDate(-n)),
        max: (n: number) => getEndingTimeOfDate(offsetDate(-n)),
    },
    nthMonth: {
        min: (n: number) => getStartingTimeOfDate(offsetMonth(-n)),
        max: (n: number) => getEndingTimeOfDate(offsetMonth(-n)),
    },
};

export const CommonDateRange = {
    last7Days: {
        min: () => getStartingTimeOfDate(addDaysOnDate(new Date(), -7)),
        max: () => getEndingTimeOfDate(new Date()),
    },
    lastMonth: {
        min: () =>
            getStartingTimeOfDate(
                new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() - 1,
                    1,
                ),
            ),
        max: () =>
            getEndingTimeOfDate(
                new Date(new Date().getFullYear(), new Date().getMonth(), 0),
            ),
    },
    currentMonth: {
        min: () =>
            getStartingTimeOfDate(
                new Date(new Date().getFullYear(), new Date().getMonth(), 1),
            ),
        max: () =>
            getEndingTimeOfDate(
                new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() + 1,
                    0,
                ),
            ),
    },
};

export function toValidDate<Fallback = undefined>(
    input: unknown,
    fallback: Fallback = undefined as Fallback,
): Date | Fallback {
    if (!input) return fallback;
    const date = new Date(input as any);
    return isNaN(date.getTime()) ? fallback : date;
}

const allowedUnits = ["days", "hours", "mins", "secs", "milliseconds"] as const;
type TimeUnit = typeof allowedUnits[number];
type TimeUnitRecord = Record<TimeUnit, number>;

export function calculateDateDifferenceUnits(
    date1: Date,
    date2: Date,
    units: TimeUnit[] = ["days", "hours", "mins", "secs"],
): TimeUnitRecord {
    let diffInMs = Math.abs(date1.getTime() - date2.getTime());

    const unitValues: TimeUnitRecord = {
        days: 0,
        hours: 0,
        mins: 0,
        secs: 0,
        milliseconds: 0,
    };

    if (units.includes("days")) {
        unitValues.days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        diffInMs -= unitValues.days * (1000 * 60 * 60 * 24);
    }

    if (units.includes("hours")) {
        unitValues.hours = Math.floor(diffInMs / (1000 * 60 * 60));
        diffInMs -= unitValues.hours * (1000 * 60 * 60);
    }

    if (units.includes("mins")) {
        unitValues.mins = Math.floor(diffInMs / (1000 * 60));
        diffInMs -= unitValues.mins * (1000 * 60);
    }

    if (units.includes("secs")) {
        unitValues.secs = Math.floor(diffInMs / 1000);
        diffInMs -= unitValues.secs * 1000;
    }

    if (units.includes("milliseconds")) {
        unitValues.milliseconds = diffInMs;
    }

    return unitValues;
}

export function formatDateDifferenceSelectedUnits(
    date1: Date,
    date2: Date,
    units: TimeUnit[] = ["days", "hours", "mins", "secs"],
): string {
    const unitValues = calculateDateDifferenceUnits(date1, date2, units);
    return mergeTimeUnits(unitValues, units);
}

export const mergeTimeUnits = (
    record: TimeUnitRecord,
    units: TimeUnit[] = ["days", "hours", "mins", "secs"],
) => {
    const parts: string[] = [];

    for (const unit of units) {
        const value = record[unit];
        if (value > 0) {
            parts.push(`${value} ${unit.slice(0, -1)}${value > 1 ? "s" : ""}`);
        }
    }

    return parts.length ? parts.join(" ") : "";
};
